import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AppThemeProvider } from "./context/AppThemeContext";
import { I18nProvider } from "./context/I18nContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import EventLayout from "./components/EventLayout";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AdminDashboard from "./pages/AdminDashboard";
import EventsPage from "./pages/EventsPage";
import DirectorPage from "./pages/DirectorPage";
import JudgePage from "./pages/JudgePage";
import DisplayPage from "./pages/DisplayPage";
import CountdownDisplayPage from "./pages/CountdownDisplayPage";
import BracketSetupPage from "./pages/BracketSetupPage";

const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  return <Navigate to="/events" replace />;
};

function App() {
  return (
    <I18nProvider>
      <AppThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" />
          <Routes>
            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Home redirect */}
            <Route path="/" element={<HomeRedirect />} />
            
            {/* Admin (super_admin only) */}
            <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
            
            {/* Events selector (standard users) */}
            <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
            
            {/* Event-scoped pages (protected: director needs auth, display/judge are public) */}
            <Route path="/event/:slug/director" element={
              <ProtectedRoute>
                <EventLayout>
                  <ThemeProvider>
                    <DirectorPage />
                  </ThemeProvider>
                </EventLayout>
              </ProtectedRoute>
            } />
            <Route path="/event/:slug/bracket-setup" element={
              <ProtectedRoute>
                <EventLayout>
                  <ThemeProvider>
                    <BracketSetupPage />
                  </ThemeProvider>
                </EventLayout>
              </ProtectedRoute>
            } />
            
            {/* Public event pages (display + judge) */}
            <Route path="/event/:slug/display" element={
              <EventLayout>
                <ThemeProvider>
                  <DisplayPage />
                </ThemeProvider>
              </EventLayout>
            } />
            <Route path="/event/:slug/countdown" element={
              <EventLayout>
                <ThemeProvider>
                  <CountdownDisplayPage />
                </ThemeProvider>
              </EventLayout>
            } />
            <Route path="/event/:slug/judge/:judgeNumber" element={
              <EventLayout>
                <ThemeProvider>
                  <JudgePage />
                </ThemeProvider>
              </EventLayout>
            } />
            
            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </AppThemeProvider>
    </I18nProvider>
  );
}

export default App;
